"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks/useAuth";
import { channelsAPI, handleApiError } from "../../../services/api";
import { resolveImageUrl, withPlaceholder } from "../../../utils/imageUtils";
import "./CreateChannelModal.css";

const CreateChannelModal = ({ isOpen, onClose, onChannelCreated }) => {
  const { t } = useTranslation();
  const { getUserType } = useAuth();
  
  // Get user type color
  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8',
      'VETERINARIAN': '#00D4B3',
      'SERVICE_PROVIDER': '#D65745',
      'SERVICE_PROVIDER': '#D65745',
      'CLINIC_MODERATOR': '#2A8AD2',
    };
    return userTypeConfig[userType] || '#8548A8';
  };
  const userTypeColor = getUserTypeColor(currentUserType);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    profileImage: null,
    privacySetting: "public" // public, private
  });
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };


  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, profileImage: t("createChannelModal.errors.imageType") }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, profileImage: t("createChannelModal.errors.imageSize", { size: 5 }) }));
        return;
      }
      setFormData(prev => ({ ...prev, profileImage: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = t("createChannelModal.errors.nameRequired");
    } else if (formData.name.trim().length < 3) {
      newErrors.name = t("createChannelModal.errors.nameMin");
    } else if (formData.name.trim().length > 100) {
      newErrors.name = t("createChannelModal.errors.nameMax");
    }
    if (!formData.description.trim()) {
      newErrors.description = t("createChannelModal.errors.descriptionRequired");
    } else if (formData.description.trim().length < 10) {
      newErrors.description = t("createChannelModal.errors.descriptionMin");
    } else if (formData.description.trim().length > 500) {
      newErrors.description = t("createChannelModal.errors.descriptionMax");
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const requestData = {
        name: formData.name,
        description: formData.description,
        privacy_setting: formData.privacySetting,
        profileImage: formData.profileImage
      };

      const response = await channelsAPI.createChannel(requestData);

      if (response.data && response.data.data) {
        const responseData = response.data.data;
        const profileImageUrl = resolveImageUrl(
          responseData.profile_image_url,
          responseData.profile_image,
          responseData.profileImageUrl,
          responseData.profileImage,
          responseData.profile_image_path
        ) || profileImagePreview;

        const ownerProfileImage = resolveImageUrl(
          responseData.owner?.profile_image_url,
          responseData.owner?.profile_image,
          responseData.owner?.avatar,
          responseData.owner?.profileImage,
          responseData.owner?.profile_image_path
        );

        const newChannel = {
          id: responseData.id,
          name: responseData.name,
          description: responseData.description,
          profileImage: withPlaceholder(profileImageUrl, "/images/social/pic-placeholder.png"),
          memberCount: responseData.members_count || 1,
          postCount: responseData.posts_count || 0,
          isPublic: responseData.privacy_setting === 'public',
          privacySetting: responseData.privacy_setting,
          isJoined: true,
          role: "owner",
          owner: {
            id: responseData.owner?.id,
            name: responseData.owner?.name || responseData.owner?.username || t("createChannelModal.ownerFallback"),
            username: responseData.owner?.username || "@currentuser",
            profileImage: withPlaceholder(ownerProfileImage, "/icons/person.png")
          }
        };

        onChannelCreated(newChannel);
        
        setFormData({
          name: "",
          description: "",
          profileImage: null,
          privacySetting: "public"
        });
        setProfileImagePreview(null);
        setErrors({});
      }
    } catch (error) {
      handleApiError(error);
      if (error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        setErrors({
          name: apiErrors.name?.[0] || null,
          description: apiErrors.description?.[0] || null,
          profileImage: apiErrors.profile_image?.[0] || null
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-channel-modal-overlay" onClick={onClose}>
      <div className="create-channel-modal" onClick={(e) => e.stopPropagation()} style={{ '--user-type-color': userTypeColor }}>
        <div className="create-channel-modal-header">
          <h2 className="create-channel-modal-title">{t("createChannelModal.title")}</h2>
          <button className="create-channel-modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="create-channel-modal-form">
          <div className="create-channel-form-grid">
            <div className="create-channel-form-column">
              <div className="create-channel-form-group">
                <label className="create-channel-form-label">{t("createChannelModal.fields.nameLabel")}</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t("createChannelModal.fields.namePlaceholder")}
                  className={`create-channel-form-input ${errors.name ? "error" : ""}`}
                  maxLength={100}
                  minLength={3}
                  required
                />
                {errors.name && <span className="create-channel-form-error">{errors.name}</span>}
              </div>

              <div className="create-channel-form-group">
                <label className="create-channel-form-label">{t("createChannelModal.fields.descriptionLabel")}</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder={t("createChannelModal.fields.descriptionPlaceholder")}
                  className={`create-channel-form-textarea ${errors.description ? "error" : ""}`}
                  rows="6"
                  maxLength={500}
                  minLength={10}
                  required
                />
                {errors.description && <span className="create-channel-form-error">{errors.description}</span>}
              </div>

              <div className="create-channel-form-group">
                <label className="create-channel-form-label">{t("createChannelModal.fields.privacyLabel")}</label>
                <div className="create-channel-privacy-options">
                  <label className="create-channel-privacy-option">
                    <input
                      type="radio"
                      name="privacySetting"
                      value="public"
                      checked={formData.privacySetting === "public"}
                      onChange={handleInputChange}
                    />
                    <div className="privacy-option-content">
                      <div className="privacy-option-header">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="M12 2a15 15 0 0 0 0 20"></path>
                          <path d="M12 2a15 15 0 0 1 0 20"></path>
                        </svg>
                        <span>{t("createChannelModal.privacy.publicTitle")}</span>
                      </div>
                      <p>{t("createChannelModal.privacy.publicDescription")}</p>
                    </div>
                  </label>
                  <label className="create-channel-privacy-option">
                    <input
                      type="radio"
                      name="privacySetting"
                      value="private"
                      checked={formData.privacySetting === "private"}
                      onChange={handleInputChange}
                    />
                    <div className="privacy-option-content">
                      <div className="privacy-option-header">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                        <span>{t("createChannelModal.privacy.privateTitle")}</span>
                      </div>
                      <p>{t("createChannelModal.privacy.privateDescription")}</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="create-channel-form-column">
              <div className="create-channel-form-group">
                <label className="create-channel-form-label">{t("createChannelModal.fields.profileLabel")}</label>
                <label className="create-channel-image-upload profile dropzone">
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleProfileImageChange}
                  />
                  <div className="create-channel-profile-preview">
                    {profileImagePreview ? (
                      <img src={profileImagePreview} alt={t("createChannelModal.upload.profilePreviewAlt")} />
                    ) : (
                      <div className="create-channel-profile-placeholder">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                        <span>{t("createChannelModal.placeholders.profile")}</span>
                      </div>
                    )}
                  </div>
                  {!profileImagePreview && (
                    <span className="create-channel-drop-hint">
                      {t("createChannelModal.upload.profileUpload", { defaultValue: "UPLOAD PROFILE" })}
                    </span>
                  )}
                </label>
                {errors.profileImage && <span className="create-channel-form-error">{errors.profileImage}</span>}
              </div>
            </div>
          </div>

          <div className="create-channel-modal-actions">
            <button type="button" className="create-channel-modal-cancel" onClick={onClose}>
              {t("createChannelModal.buttons.cancel")}
            </button>
            <button type="submit" className="create-channel-modal-submit" disabled={isSubmitting}>
              {isSubmitting ? t("createChannelModal.buttons.submitting") : t("createChannelModal.buttons.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateChannelModal;

